import { Link, Navigate } from 'react-router-dom';
import { Sparkles, ShoppingBag, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function Welcome() {
  const { user, loading } = useAuth();

  // If user is authenticated, redirect to dashboard
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-accent-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-600 rounded-full mb-6">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-surface-900 mb-4">
            Welcome to Virtual Fitting Room
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto">
            Transform your WooCommerce store with AI-powered virtual try-ons.
            Let your customers see how products look on them before they buy.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <FeatureCard
            icon={<ShoppingBag className="w-8 h-8" />}
            title="Boost Conversions"
            description="Increase sales by letting customers visualize products on themselves"
            color="bg-brand-500"
          />
          <FeatureCard
            icon={<TrendingUp className="w-8 h-8" />}
            title="Reduce Returns"
            description="Help customers make confident purchase decisions with accurate previews"
            color="bg-accent-500"
          />
          <FeatureCard
            icon={<Users className="w-8 h-8" />}
            title="Engage Shoppers"
            description="Create an interactive shopping experience that keeps customers coming back"
            color="bg-pink-500"
          />
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-panel p-8 mb-12">
          <h2 className="text-3xl font-bold text-surface-900 mb-6 text-center">
            How It Works
          </h2>
          <div className="space-y-6">
            <Step
              number="1"
              title="Sign Up"
              description="Create your account and connect your WooCommerce store"
            />
            <Step
              number="2"
              title="Choose Your Plan"
              description="Select a subscription tier that fits your store's needs"
            />
            <Step
              number="3"
              title="Install Plugin"
              description="Install our plugin and it automatically connects"
            />
            <Step
              number="4"
              title="Go Live"
              description="Enable virtual try-on on your products and watch conversions grow"
            />
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/signup"
            className="inline-flex items-center px-8 py-4 bg-brand-600 text-white text-lg font-semibold rounded-xl hover:bg-brand-700 transition-colors shadow-card hover:shadow-panel"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
          <p className="mt-4 text-surface-600">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, color }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-card hover:shadow-panel transition-shadow">
      <div className={`${color} w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4`}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-surface-900 mb-2">{title}</h3>
      <p className="text-surface-600">{description}</p>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex items-start">
      <div className="flex-shrink-0 w-10 h-10 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
        {number}
      </div>
      <div className="ml-4">
        <h3 className="text-lg font-semibold text-surface-900 mb-1">{title}</h3>
        <p className="text-surface-600">{description}</p>
      </div>
    </div>
  );
}

